import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { Link, useParams, useNavigate } from 'react-router-dom';
import useUrlParams from '../hooks/useUrlParams';
import { db } from '../firebase';
import { Committee, CommitteeLobbyist, CommitteeNewsArticle, CommitteeRecentSession, MemberIndexEntry, Party, SecondaryLawRow, SecondaryLawStats } from '../types/types';
import Layout from '../components/Layout';
import { appearancesLabel } from '../utils/format';
import { shortPartyName } from '../utils/party';
import { useOnAir } from '../components/live/useOnAir';
import CommitteeLiveBanner from '../components/live/CommitteeLiveBanner';
import { addDaysYmd, israelNow } from '../utils/schedule';
import {
  getAgendaMotions, getCommittee, getCommitteeNews, getMemberSummaries, getParties, getSecondaryLaws, getSecondaryLawStats, UNTAGGED_KNESSET,
} from '../data/cache';
import { loadFavoriteCommittees, saveFavoriteCommittees } from '../utils/favorites';
import { photoFor } from '../utils/names';
import { buttonReset, color, font, radius, space, text, Tone, weight } from '../theme';
import DetailHeader from '../components/DetailHeader';
import TabBar from '../components/TabBar';
import { MOBILE_TOP_BAR_HEIGHT } from '../components/MobileShell';
import { Card, CardHeader, Section } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Badge, StageBadge } from '../components/Badge';
import { SegmentBar } from '../components/Bar';
import Bar from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import SearchField from '../components/SearchField';
import Button, { ButtonLink, IconButton } from '../components/Button';
import Icon from '../components/Icon';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText } from '../components/Skeleton';
import SecondaryLawList from '../components/SecondaryLawList';
import CommitteeAgendaMotions from '../components/agenda/CommitteeAgendaMotions';
import { useBreakpoint } from '../useBreakpoint';
import PageContainer from '../layout/PageContainer';
import DetailHeaderFrame from '../layout/DetailHeaderFrame';
import TwoPane from '../layout/TwoPane';

/* ── Config ─────────────────────────────────────────────────────────────── */

const typeConfig: Record<string, { label: string; tone: Tone }> = {
  standing: { label: 'ועדה קבועה',  tone: 'info' },
  special:  { label: 'ועדה מיוחדת', tone: 'neutral' },
  joint:    { label: 'ועדה משותפת', tone: 'neutral' },
};

type CmteTab = 'overview' | 'sessions' | 'activity' | 'news' | 'lobbyists';

// The "פעילות" tab's switch: secondary legislation, or motions for the agenda
type ActivitySeg = 'seclaw' | 'motions';

// Committee protocols appear about 3 months after the session; until then (and a little
// longer) a past session without one says so
const PROTOCOL_PENDING_DAYS = 120;

interface RelatedBill {
  id: string;
  title: string;
  simpleStage?: string;
}

interface CommitteeMember extends MemberIndexEntry {
  partyName: string;
  partyColor: string;
}

// Scraped article HTML → plain text. Never rendered as HTML; paragraph and line
// breaks are kept as newlines for a pre-wrap container.
const htmlToText = (html: string): string => {
  const withBreaks = html
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n');
  const parsed = new DOMParser().parseFromString(withBreaks, 'text/html');
  const junk = parsed.querySelectorAll('script, style');
  for (let i = 0; i < junk.length; i++) junk[i].remove();
  return (parsed.body.textContent || '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Lobbyists per committee member: high / medium / low (status colors, not clickable)
const pressureLevel = (ratio: number): { label: string; tone: Tone; bar: string } =>
  ratio >= 3 ? { label: 'גבוה', tone: 'danger', bar: '#E24B4A' }
    : ratio >= 1 ? { label: 'בינוני', tone: 'warning', bar: '#C77700' }
      : { label: 'נמוך', tone: 'success', bar: '#3f7a16' };

const formatDate = (iso: string) => (iso ? new Date(iso).toLocaleDateString('he-IL') : '');

/* ── Page ───────────────────────────────────────────────────────────────── */

const CommitteeDetailPage = () => {
  const { committeeId } = useParams();
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const [searchParams, setSearchParams] = useUrlParams(); // keeps the caller's `from` across tab switches

  const [committee, setCommittee] = useState<Committee | null>(null);
  // A committee's lobbyists come from the sessions of its own Knesset
  const lobbyistsKnesset = (committee && committee.knessetNumber) || 25;
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [members, setMembers] = useState<CommitteeMember[] | null>(null);
  const [substitutes, setSubstitutes] = useState<CommitteeMember[]>([]);
  const [observers, setObservers] = useState<CommitteeMember[]>([]);
  const [chairName, setChairName] = useState('');
  const [relatedBills, setRelatedBills] = useState<RelatedBill[]>([]);
  const [newsArticles, setNewsArticles] = useState<CommitteeNewsArticle[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(loadFavoriteCommittees);
  const [lobbySearch, setLobbySearch] = useState('');
  const [expandedArticle, setExpandedArticle] = useState<number | null>(null);
  // What's on air (the live feed merged with meta/scheduleNow, re-read every minute), for the live banner
  const onAir = useOnAir();
  // "פעילות": secondary-law stats (undefined until read, null if missing), the committee's
  // rows (null until read), and the number of motions referred to it (once that list is read)
  const [slStats, setSlStats] = useState<SecondaryLawStats | null | undefined>(undefined);
  const [slRows, setSlRows] = useState<SecondaryLawRow[] | null>(null);
  const [slError, setSlError] = useState(false);
  const [slReload, setSlReload] = useState(0);
  const [motionCount, setMotionCount] = useState<number | null>(null);
  // Where the tab bar sits in the page before it sticks (phone), to scroll back to it
  const tabsAnchorRef = useRef<HTMLDivElement>(null);

  // The committee's Knesset, for the per-Knesset lists (an archived Knesset's committee reads its archive)
  const k = committee ? committee.knessetNumber ?? UNTAGGED_KNESSET : UNTAGGED_KNESSET;

  // The tab lives in the URL, so Back returns to it
  const tabParam = searchParams.get('tab');
  const requestedTab: CmteTab = tabParam === 'sessions' || tabParam === 'activity' || tabParam === 'news' || tabParam === 'lobbyists' ? tabParam : 'overview';
  const activeTab: CmteTab = requestedTab === 'news' && newsArticles.length === 0 ? 'overview' : requestedTab;
  const activitySeg: ActivitySeg = searchParams.get('seg') === 'motions' ? 'motions' : 'seclaw';
  const setActiveTab = (tab: CmteTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') next.delete('tab'); else next.set('tab', tab);
      // The switch and the list filters belong to the "פעילות" tab
      if (tab !== 'activity') { next.delete('seg'); next.delete('kind'); }
      return next;
    }, { replace: true });
    // Phone: switching tabs while scrolled down starts the new tab right under the stuck tab bar
    const anchor = tabsAnchorRef.current;
    if (!isDesktop && anchor) {
      const top = anchor.getBoundingClientRect().top + window.scrollY - MOBILE_TOP_BAR_HEIGHT;
      if (window.scrollY > top) window.scrollTo({ top });
    }
  };
  const setActivitySeg = (seg: ActivitySeg) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (seg === 'seclaw') next.delete('seg'); else next.set('seg', seg);
    next.delete('kind');
    return next;
  }, { replace: true });

  // The committee doc first; members, parties, bills and news after, without blocking it
  useEffect(() => {
    if (!committeeId) return;
    let cancelled = false;
    setStatus('loading');
    getCommittee(committeeId).then(cmte => {
      if (cancelled) return;
      if (!cmte) { setStatus('notFound'); return; }
      setCommittee(cmte);
      setStatus('ready');

      // Full members, substitutes (ממלאי מקום) and observers are separate lists; only full
      // members count as the committee's members
      const allIds = (cmte.memberIds || []).concat(cmte.substituteIds || [], cmte.observerIds || []);
      if (cmte.currentChairId) allIds.push(cmte.currentChairId);
      Promise.all([getMemberSummaries(allIds), getParties()]).then(([summaries, parties]) => {
        if (cancelled) return;
        const partyById: Record<string, Party> = {};
        for (const p of parties) partyById[p.id] = p;
        const toList = (ids: string[] | undefined): CommitteeMember[] => (ids || [])
          .map(id => summaries[id])
          // Skip departed MKs still listed on the committee
          .filter((m): m is MemberIndexEntry => Boolean(m) && m.isCurrentMk !== false)
          .map(m => {
            const p = m.partyId ? partyById[m.partyId] : undefined;
            return { ...m, partyName: p ? shortPartyName(p.name || '') : '', partyColor: p?.color || '#98a2b3' };
          });
        setMembers(toList(cmte.memberIds).sort((a, b) => (a.id === cmte.currentChairId ? -1 : b.id === cmte.currentChairId ? 1 : 0)));
        setSubstitutes(toList(cmte.substituteIds));
        setObservers(toList(cmte.observerIds));
        const chair = cmte.currentChairId ? summaries[cmte.currentChairId] : undefined;
        setChairName(chair?.name || '');
      }).catch(() => { if (!cancelled) setMembers([]); });

      getDocs(query(
        collection(db, 'bills'),
        where('committeeId', '==', cmte.id),
        where('simpleStage', '==', 'בוועדה'),
        limit(20),
      )).then(snap => {
        if (!cancelled) setRelatedBills(snap.docs.map(d => ({ id: d.id, title: d.data().title, simpleStage: d.data().simpleStage })));
      }).catch(() => { /* ok — optional */ });

      getCommitteeNews(cmte.id).then(articles => {
        if (!cancelled) setNewsArticles(articles);
      }).catch(() => { /* ok — optional */ });
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [committeeId, reloadKey]);

  // Live banner (decision 1E): the committee's session on air, a live one before one on a break
  const liveCard = committee && onAir.ready
    ? onAir.merged.onAir.filter(c => c.committeeDocId === committee.id)[0] || null
    : null;

  // Scroll restoration: save on unmount, restore once the content has loaded
  const restored = useRef(false);
  useEffect(() => {
    if (!committeeId) return;
    const key = `cmte_scroll_${committeeId}`;
    return () => {
      try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* ok */ }
    };
  }, [committeeId]);
  useEffect(() => {
    if (!committeeId || restored.current || members === null) return;
    restored.current = true;
    const key = `cmte_scroll_${committeeId}`;
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) {
        window.scrollTo(0, parseInt(saved, 10) || 0);
        sessionStorage.removeItem(key);
      }
    } catch { /* ok */ }
  }, [committeeId, members]);

  /* ── "פעילות": loaded when the tab opens ── */

  // Secondary-law totals (~2 KB): the switch's count, and whether the list is worth reading
  useEffect(() => {
    if (activeTab !== 'activity' || !committee || slStats !== undefined) return;
    let cancelled = false;
    getSecondaryLawStats(k)
      .then(stats => { if (!cancelled) setSlStats(stats); })
      .catch(() => { if (!cancelled) setSlStats(null); });
    return () => { cancelled = true; };
  }, [activeTab, committee, slStats, k]);

  // The committee's secondary legislation, from the Knesset's list (~1.8 MB, cached across
  // pages); skipped when the totals say the committee has none
  useEffect(() => {
    if (activeTab !== 'activity' || activitySeg !== 'seclaw' || !committee || slRows !== null || slStats === undefined) return;
    if (slStats && !slStats.byCommittee[committee.id]) { setSlRows([]); return; }
    let cancelled = false;
    setSlError(false);
    getSecondaryLaws(k)
      .then(list => { if (!cancelled) setSlRows(list.filter(r => r.committeeId === committee.id)); })
      .catch(() => { if (!cancelled) setSlError(true); });
    return () => { cancelled = true; };
  }, [activeTab, activitySeg, committee, slRows, slStats, k, slReload]);

  // The motions referred to the committee, counted once their segment opens (the motions
  // component reads the same cached list); the stats doc has no per-committee totals
  useEffect(() => {
    if (activeTab !== 'activity' || activitySeg !== 'motions' || !committee || motionCount !== null) return;
    let cancelled = false;
    getAgendaMotions(k)
      .then(list => {
        if (cancelled) return;
        let n = 0;
        for (let i = 0; i < list.length; i++) if (list[i].committeeId === committee.id) n++;
        setMotionCount(n);
      })
      .catch(() => { /* ok — the count is optional; the list shows its own error */ });
    return () => { cancelled = true; };
  }, [activeTab, activitySeg, committee, motionCount, k]);

  const toggleFavorite = () => {
    if (!committeeId) return;
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(committeeId)) next.delete(committeeId); else next.add(committeeId);
      saveFavoriteCommittees(next);
      return next;
    });
  };

  const committeeLobbyists: CommitteeLobbyist[] = useMemo(() => committee?.lobbyists || [], [committee]);
  const sessions: CommitteeRecentSession[] = committee?.recentSessions || [];

  const filteredLobbyists = useMemo(() => {
    const q = lobbySearch.trim().toLowerCase();
    if (!q) return committeeLobbyists;
    return committeeLobbyists.filter(l =>
      l.name.toLowerCase().includes(q) || l.organizations.some(o => o.toLowerCase().includes(q))
    );
  }, [committeeLobbyists, lobbySearch]);

  // Party breakdown, largest first, in each party's own color
  const partyBreakdown = useMemo(() => {
    const byParty: Record<string, { key: string; name: string; partyId?: string; color: string; count: number }> = {};
    for (const m of members || []) {
      const key = m.partyId || 'none';
      if (!byParty[key]) byParty[key] = { key, name: m.partyName || 'לא ידוע', partyId: m.partyId, color: m.partyColor, count: 0 };
      byParty[key].count++;
    }
    return Object.keys(byParty).map(k => byParty[k]).sort((a, b) => b.count - a.count);
  }, [members]);

  /* ── Loading, error, not found ── */

  if (status === 'loading') {
    return (
      <Layout>
        <div data-skeleton="true" style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><Skeleton width={80} height={20} /><Skeleton width="80%" height={24} /><SkeletonText lines={1} /></div></Card>
          <SkeletonList rows={5} avatar />
        </div>
      </Layout>
    );
  }
  if (status === 'error') {
    return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  }
  if (status === 'notFound' || !committee) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="users" title="הוועדה לא נמצאה" body="ייתכן שהקישור שגוי או שהוועדה כבר לא פעילה." action={<Button onClick={() => navigate('/committees', { replace: true })}>לכל הוועדות</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  const type = typeConfig[committee.type] || typeConfig.standing;
  const isFav = committeeId ? favorites.has(committeeId) : false;
  const memberCount = members ? members.length : (committee.memberIds || []).length;
  // Sessions: a past session from the last few months whose protocol isn't published yet
  // (cancelled sessions never get one)
  const now = israelNow();
  const protocolCutoff = addDaysYmd(now.slice(0, 10), -PROTOCOL_PENDING_DAYS);
  const isProtocolPending = (s: CommitteeRecentSession): boolean =>
    !s.protocolUrl && s.status !== 'מבוטלת' && !!s.date && s.date < now && s.date.slice(0, 10) >= protocolCutoff;
  const tabs: { key: CmteTab; label: string; count?: number }[] = [
    { key: 'overview', label: 'סקירה' },
    { key: 'sessions', label: 'ישיבות', count: sessions.length },
    // Always third, so the first three tabs keep their places whether or not there is news
    { key: 'activity', label: 'פעילות' },
  ];
  if (newsArticles.length > 0) tabs.push({ key: 'news', label: 'חדשות', count: newsArticles.length });
  tabs.push({ key: 'lobbyists', label: 'שדלנים', count: committeeLobbyists.length });

  // Equal-width underline tabs, as on the MK page
  const tabBar = <TabBar variant="underline" label="לשוניות הוועדה" tabs={tabs} active={activeTab} onChange={setActiveTab} />;

  return (
    <Layout width="full" breadcrumbs={[{ label: committee.name }]}>
      <PageContainer>
      {/* Desktop: the header card is a sticky side panel; the tabs sit above the main column so
          the panel lines up with the first card. Phone/tablet: header, then tabs and content. */}
      <TwoPane
        sideWidth={320}
        mainHeader={isDesktop ? <div style={{ marginBottom: space.lg }}>{tabBar}</div> : undefined}
        side={<DetailHeaderFrame>
      <DetailHeader
        fallback="/committees"
        fallbackLabel="ועדות"
        actions={<IconButton onDark icon={isFav ? 'starFilled' : 'star'} label={isFav ? 'הסרה מהמעקב' : 'הוספה למעקב'} pressed={isFav} onClick={toggleFavorite} />}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
          <span style={{ display: 'flex', gap: space.xs + 2, flexWrap: 'wrap' }}>
            <Badge tone={type.tone}>{type.label}</Badge>
            {committee.isCurrent === false && <Badge>הסתיימה</Badge>}
          </span>
          <h1 style={text.entityTitle}>{committee.name}</h1>
          <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.6 }}>
            {committee.currentChairId && chairName
              ? <>יו״ר: <Link to={`/members/${committee.currentChairId}`} state={{ from: committee.name }} style={{ color: color.primary, fontWeight: weight.medium, textDecoration: 'none' }}>{chairName}</Link></>
              : committee.currentChairId ? 'יו״ר: …' : 'יו״ר: טרם מונה'}
            {' · '}{memberCount} חברים
            {(committee.substituteIds || []).length > 0 && ` · ${(committee.substituteIds || []).length} ממלאי מקום`}
            {sessions.length > 0 && ` · ${sessions.length} ישיבות`}
          </div>
          {committee.email && (
            <a href={`mailto:${committee.email}`} style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs + 2, fontSize: font.small, color: color.primary, textDecoration: 'none', minHeight: 32 }}>
              <Icon name="mail" size={16} />{committee.email}
            </a>
          )}
          {liveCard && <CommitteeLiveBanner card={liveCard} />}
        </div>
      </DetailHeader>
        </DetailHeaderFrame>}
      >

      <div style={{ padding: isDesktop ? 0 : space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {/* Phone: the tab bar sticks under the top bar, on a full-width strip. Both are direct
            children of this column, so the bar can stick for the whole length of the tab
            (the anchor's negative margin cancels the column gap it would add). */}
        {!isDesktop && <div ref={tabsAnchorRef} aria-hidden="true" style={{ marginBottom: -space.lg }} />}
        {!isDesktop && (
          <div style={{ position: 'sticky', top: MOBILE_TOP_BAR_HEIGHT, zIndex: 40, background: 'rgba(238,242,247,0.94)', margin: `-${space.md}px -${space.lg}px -${space.sm}px`, padding: `${space.md}px ${space.lg}px ${space.sm}px` }}>
            {tabBar}
          </div>
        )}

        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === 'overview' && (
          <>
            <Card>
              <CardHeader title="על הוועדה" />
              {committee.description
                ? <p style={{ ...text.body, margin: 0, lineHeight: 1.7 }}>{committee.description}</p>
                : <p style={{ margin: 0, fontSize: font.small, color: color.textTertiary }}>תיאור הוועדה יתווסף בקרוב.</p>}
            </Card>

            <StatTileRow>
              <StatTile value={memberCount} label="חברים" />
              <StatTile value={members ? partyBreakdown.length : '…'} label="סיעות" />
              <StatTile value={sessions.length} label="ישיבות" />
              {committeeLobbyists.length > 0 && <StatTile value={committeeLobbyists.length} label="שדלנים" />}
            </StatTileRow>

            {committeeLobbyists.length > 0 && memberCount > 0 && (() => {
              const ratio = committeeLobbyists.length / memberCount;
              const level = pressureLevel(ratio);
              const top = committeeLobbyists.slice(0, 3);
              return (
                <Card>
                  <CardHeader title="מדד לחץ שדלנים" action={<Badge tone={level.tone}>{level.label}</Badge>} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.md }}>
                    <Bar value={Math.min(ratio, 5)} max={5} color={level.bar} height={8} style={{ flex: 1 }} label={`${ratio.toFixed(1)} שדלנים לחבר ועדה`} />
                    <span style={{ fontSize: font.label, fontWeight: weight.bold, flexShrink: 0 }}>{ratio.toFixed(1)} לח״כ</span>
                  </div>
                  <div style={{ fontSize: font.small, color: color.textSecondary, margin: `${space.sm}px 0 ${space.md}px` }}>
                    {committeeLobbyists.length} שדלנים פעילים · {memberCount} חברי ועדה
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
                    {top.map((l, i) => (
                      l.id
                        ? <Link key={i} to={`/lobbyists/${l.id}`} state={{ from: committee.name }} style={{ fontSize: font.small, color: color.primary, background: color.primarySoft, borderRadius: radius.pill, padding: '4px 10px', textDecoration: 'none' }}>{l.name} ({l.appearances})</Link>
                        : <Badge key={i} shape="pill">{l.name} ({l.appearances})</Badge>
                    ))}
                    {committeeLobbyists.length > 3 && (
                      <button type="button" onClick={() => setActiveTab('lobbyists')} style={{ ...buttonReset, fontSize: font.small, color: color.primary, fontWeight: weight.medium, padding: '4px 6px' }}>
                        ועוד {committeeLobbyists.length - 3}
                      </button>
                    )}
                  </div>
                </Card>
              );
            })()}

            {partyBreakdown.length > 1 && (
              <Card>
                <CardHeader title="הרכב סיעתי" />
                <SegmentBar height={10} segments={partyBreakdown.map(p => ({ key: p.key, value: p.count, color: p.color }))} label={partyBreakdown.map(p => `${p.name} ${p.count}`).join(', ')} />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${space.xs}px ${space.md}px`, marginTop: space.md }}>
                  {partyBreakdown.map(p => {
                    const content = (
                      <>
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color, flexShrink: 0 }} />
                        {p.name} ({p.count})
                      </>
                    );
                    const style: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: space.xs + 2, fontSize: font.small, color: color.text, textDecoration: 'none', minHeight: 28 };
                    return p.partyId
                      ? <Link key={p.key} to={`/parties/${p.partyId}`} state={{ from: committee.name }} style={style}>{content}</Link>
                      : <span key={p.key} style={style}>{content}</span>;
                  })}
                </div>
              </Card>
            )}

            <Section title="חברי הוועדה" meta={members ? members.length : undefined}>
              {members === null
                ? <SkeletonList rows={5} avatar />
                : members.length === 0
                  ? <Card padding={0}><EmptyState icon="users" title="אין חברים רשומים" /></Card>
                  : (
                    <Card padding={0}>
                      {members.map((m, i) => (
                        <ListRow
                          key={m.id}
                          divider={i > 0}
                          to={`/members/${m.id}`}
                          state={{ from: committee.name }}
                          leading={<Avatar name={m.name} src={photoFor(m, 40)} size={40} />}
                          title={<span style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>{m.name}{m.id === committee.currentChairId && <Badge tone="info">יו״ר</Badge>}</span>}
                          subtitle={m.partyName || m.role}
                        />
                      ))}
                    </Card>
                  )}
            </Section>

            {/* Substitutes and observers: listed apart and left out of the member counts */}
            {[
              { title: 'ממלאי מקום', list: substitutes },
              { title: 'משקיפים', list: observers },
            ].filter(g => g.list.length > 0).map(g => (
              <Section key={g.title} title={g.title} meta={g.list.length}>
                <Card padding={0}>
                  {g.list.map((m, i) => (
                    <ListRow
                      key={m.id}
                      divider={i > 0}
                      to={`/members/${m.id}`}
                      state={{ from: committee.name }}
                      leading={<Avatar name={m.name} src={photoFor(m, 40)} size={40} />}
                      title={m.name}
                      subtitle={m.partyName || m.role}
                    />
                  ))}
                </Card>
              </Section>
            ))}

            {relatedBills.length > 0 && (
              <Section title="הצעות חוק בטיפול הוועדה" meta={relatedBills.length}>
                <Card padding={0}>
                  {relatedBills.map((b, i) => (
                    <ListRow
                      key={b.id}
                      divider={i > 0}
                      to={`/legislation/${b.id}`}
                      state={{ from: committee.name }}
                      title={b.title}
                      subtitle={<span style={{ display: 'flex', marginTop: space.xxs }}><StageBadge stage={b.simpleStage} /></span>}
                    />
                  ))}
                </Card>
              </Section>
            )}
          </>
        )}

        {/* ═══ SESSIONS TAB ═══ */}
        {activeTab === 'sessions' && (
          sessions.length === 0
            ? <Card padding={0}><EmptyState icon="calendar" title="אין ישיבות רשומות" /></Card>
            : sessions.map(session => (
              <Card key={session.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.sm, marginBottom: space.sm }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
                    {/* Upcoming sessions have no number yet (0) */}
                    <span style={{ fontSize: font.body, fontWeight: weight.bold, color: color.text }}>{session.number ? `ישיבה ${session.number}` : 'ישיבה'}</span>
                    {session.type && <Badge>{session.type}</Badge>}
                  </span>
                  <span style={{ fontSize: font.small, color: color.textSecondary }}>{formatDate(session.date)}</span>
                </div>
                {session.agendaItems && session.agendaItems.length > 0 && (
                  <ol style={{ margin: `0 0 ${space.md}px`, padding: 0, listStyle: 'none' }}>
                    {session.agendaItems.map((item, i) => (
                      <li key={i} style={{ display: 'flex', gap: space.sm, fontSize: font.label, color: color.text, lineHeight: 1.5, padding: `${space.xs + 2}px 0`, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none' }}>
                        <span style={{ fontSize: font.caption, color: color.textTertiary, flexShrink: 0, minWidth: 16, paddingTop: 2 }}>{item.ordinal}.</span>
                        {item.billDocId
                          ? <Link to={`/legislation/${item.billDocId}`} state={{ from: committee.name }} style={{ color: color.primary, textDecoration: 'none' }}>{item.name}</Link>
                          : <span>{item.name}</span>}
                      </li>
                    ))}
                  </ol>
                )}
                {isProtocolPending(session) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, fontSize: font.caption, color: color.textTertiary, lineHeight: 1.5, marginBottom: session.sessionUrl ? space.sm : 0 }}>
                    <Icon name="clock" size={14} />
                    הפרוטוקול יפורסם בדרך כלל כשלושה חודשים אחרי הישיבה
                  </div>
                )}
                {(session.protocolUrl || session.sessionUrl) && (
                  <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
                    {session.protocolUrl && <ButtonLink href={session.protocolUrl} icon="document">פרוטוקול</ButtonLink>}
                    {session.sessionUrl && <ButtonLink href={session.sessionUrl.replace(/^http:/, 'https:')} icon="external" variant="neutral">עמוד הישיבה</ButtonLink>}
                  </div>
                )}
              </Card>
            ))
        )}

        {/* ═══ ACTIVITY TAB: secondary legislation | motions for the agenda ═══ */}
        {activeTab === 'activity' && (() => {
          const slCount = slRows ? slRows.length : slStats ? slStats.byCommittee[committee.id] || 0 : undefined;
          const segBar = (
            <TabBar
              label="פעילות הוועדה"
              tabs={[
                { key: 'seclaw', label: 'חקיקת משנה', count: slCount },
                { key: 'motions', label: 'הצעות לסדר', count: motionCount === null ? undefined : motionCount },
              ]}
              active={activitySeg}
              onChange={setActivitySeg}
            />
          );
          return activitySeg === 'seclaw'
            ? (
              <SecondaryLawList
                rows={slRows}
                error={slError}
                onRetry={() => { setSlError(false); setSlReload(n => n + 1); }}
                context="committee"
                toolbarStart={segBar}
                emptyTitle="אין חקיקת משנה"
                emptyBody={`לא נמצאו תקנות, צווים או דיווחים שהועברו לוועדה בכנסת ה-${k}.`}
              />
            )
            : (
              <>
                {isDesktop ? <div style={{ maxWidth: 420 }}>{segBar}</div> : segBar}
                <CommitteeAgendaMotions committeeId={committee.id} knessetNumber={k} />
              </>
            );
        })()}

        {/* ═══ LOBBYISTS TAB ═══ */}
        {activeTab === 'lobbyists' && (
          committeeLobbyists.length === 0
            ? <Card padding={0}><EmptyState icon="users" title="אין שדלנים" body={`לא נמצאו שדלנים שהשתתפו בישיבות ועדה זו בכנסת ה-${lobbyistsKnesset}.`} /></Card>
            : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.sm, fontSize: font.small, color: color.textSecondary }}>
                  <span>{committeeLobbyists.length} שדלנים השתתפו בישיבות הוועדה בכנסת ה-{lobbyistsKnesset}</span>
                  <Link to="/committees?tab=lobbyists" style={{ color: color.primary, fontWeight: weight.medium, textDecoration: 'none', flexShrink: 0, minHeight: 32, display: 'flex', alignItems: 'center' }}>כל השדלנים</Link>
                </div>
                <SearchField value={lobbySearch} onChange={setLobbySearch} placeholder="חיפוש לפי שם או ארגון" />
                {filteredLobbyists.length === 0
                  ? <Card padding={0}><EmptyState title="לא נמצאו תוצאות" action={<Button variant="ghost" onClick={() => setLobbySearch('')}>ניקוי החיפוש</Button>} /></Card>
                  : (
                    <Card padding={0}>
                      {filteredLobbyists.map((l, i) => {
                        const tags = (l.titles || []).concat(l.organizations);
                        return (
                          <ListRow
                            key={l.id || `${l.name}-${i}`}
                            divider={i > 0}
                            to={l.id ? `/lobbyists/${l.id}` : undefined}
                            state={{ from: committee.name }}
                            title={<span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>{l.name}{l.isLoby99 && <Badge tone="info">לובי 99</Badge>}</span>}
                            subtitle={tags.length > 0 ? <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs, marginTop: space.xxs }}>{tags.map((t, j) => <Badge key={j}>{t}</Badge>)}</span> : undefined}
                            trailing={<Badge tone="info" style={{ flexShrink: 0 }}>{appearancesLabel(l.appearances)}</Badge>}
                          />
                        );
                      })}
                    </Card>
                  )}
              </>
            )
        )}

        {/* ═══ NEWS TAB ═══ */}
        {activeTab === 'news' && (
          <Card padding={0}>
            {newsArticles.map((article, i) => {
              const isOpen = expandedArticle === article.id;
              return (
                <div key={article.id} style={{ borderTop: i > 0 ? `1px solid ${color.divider}` : 'none' }}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setExpandedArticle(isOpen ? null : article.id)}
                    style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'flex-start', gap: space.md, padding: `${space.md}px ${space.lg}px`, minHeight: 56 }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: font.caption, color: color.textSecondary, marginBottom: space.xxs }}>{article.dateText || article.date}</span>
                      <span style={{ display: 'block', fontSize: font.body, fontWeight: weight.medium, lineHeight: 1.4 }}>{article.title}</span>
                    </span>
                    <span style={{ color: color.textTertiary, paddingTop: space.xs }}><Icon name={isOpen ? 'chevronUp' : 'chevronDown'} size={18} /></span>
                  </button>
                  {isOpen && (
                    <div style={{ padding: `0 ${space.lg}px ${space.lg}px`, fontSize: font.label, lineHeight: 1.7, color: color.text, whiteSpace: 'pre-wrap' }}>
                      {article.contentText || htmlToText(article.content || '')}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        )}
      </div>
      </TwoPane>
      </PageContainer>
    </Layout>
  );
};

export default CommitteeDetailPage;
